import { func, object } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import Div from '@ui/layout/Div'
import SwitcherType from '@ui/inputs/SwitcherType'
import Buttons from '@ui/elements/Buttons'
import { useTranslation } from 'react-i18next'
import { post } from '@util/xhrAPI'
import useFormData from "@hook/useFormData"
import { updateErrors, updateSterilizationCall } from "@store/slice/sterilizationSlice"

const ActiveSterilizationCall = ({ sterilizationCall, togglePhotosModal, setModalPhotoFilter }) => {
  const [t] = useTranslation('common')
  const renderFormData = useFormData()
  const dispatch = useDispatch()
  const { calls } = useSelector(state => state.sterilization)
  const { mission } = useSelector(state => state.main)
  const { activeSeason } = mission
  const selectedSeason = activeSeason.id
  const {
    callType,
    id,
    isFirstScanned,
    isSecondScanned,
    links,
    addressResponsibleCellPhone,
    addressResponsibleEmail,
    addressResponsibleFullName,
    nestsNumber,
    sterilizationAddress,
    sterilizationDistrict,
    sterilizationZipCode,
    treatmentJournals
  } = sterilizationCall

  const showPhotosModal = photoFilter => {
    setModalPhotoFilter(photoFilter)
    togglePhotosModal()
  }

  const updateJournal = journal => {
    const dataObject = { mission: mission.id, missionSeason: selectedSeason, sterilizationCall: id, ...journal }
    const scan = dataObject.isFirstScan ? 'firstScan' : 'secondScan'
    post(`${import.meta.env.VITE_API_URL}/api/treatment-journals`, renderFormData(dataObject), response => {
      const { code, errors, treatmentJournal } = response
      if (200 === code) {
        const currentUpdatedCall = { ...calls[selectedSeason][sterilizationCall.id] }
        const treatmentJournals = { ...currentUpdatedCall['treatmentJournals'] }
        treatmentJournals[scan] = treatmentJournal
        dispatch(updateSterilizationCall({ callUpdate: { ...currentUpdatedCall, treatmentJournals }, selectedSeason }))
      } else {
        dispatch(updateErrors(errors))
      }
    })
  }

  return (
    <>
      <tr className="expanded-area" style={{ display: "flex", gridColumn: "1 / -1" }}>
        <td style={{ flex: "1" }}>
          <Div cssClass='columns'>
            <Div cssClass='column'>
              <h2>{addressResponsibleFullName}</h2>
              <p>
                {`${sterilizationZipCode}, ${sterilizationAddress}, ${sterilizationDistrict}`}<br />
                {`${addressResponsibleCellPhone}, ${addressResponsibleEmail}`}<br />
                {`${t('table.CallerType')} : ${t(`table.${callType}`)}`}<br />
                {nestsNumber ? `${t('table.Nests')}: ${nestsNumber}` : ''}
              </p>
            </Div>
            <Div cssClass='column'>
              <h2>{t('titles.first_scan')}</h2>
              <Buttons
                buttons={[
                  {
                    icon: 'icon-access_time',
                    cssClass: treatmentJournals['firstScan'] && treatmentJournals['firstScan']['startedAt'] ? 'is-warning' : 'is-dark',
                    text: `${t('actions.started_at')}: ${treatmentJournals['firstScan'] ? treatmentJournals['firstScan']['startedAt'] : ''}`,
                    callback: () => updateJournal({ isFirstScan: true, isStart: true })
                  }, {
                    icon: 'icon-access_time',
                    cssClass: 'undefined' === typeof treatmentJournals['firstScan']
                      ? 'is-hidden'
                      : (treatmentJournals['firstScan'] && treatmentJournals['firstScan']['endedAt'] ? 'is-warning' : 'is-dark'),
                    text: `${t('actions.ended_at')}: ${treatmentJournals['firstScan'] ? treatmentJournals['firstScan']['endedAt'] : ''}`,
                    callback: () => updateJournal({ isFirstScan: true, isEnd: true, id: treatmentJournals['firstScan']['id'] })
                  }
                ]} />
              <SwitcherType
                name='isFirstScanned'
                value={'undefined' !== typeof treatmentJournals['firstScan'] ? treatmentJournals['firstScan']['isDone'] : false}
                onClick={() => updateJournal({
                  isFirstScan: true,
                  isDone: true,
                  id: treatmentJournals['firstScan'] ? treatmentJournals['firstScan']['id'] : null
                })}
                label={t('labels.is_first_scanned')}
                id={`is-${id}-first-scanned`}
              />
              <Buttons buttons={{
                icon: isFirstScanned ? 'icon-done' : 'icon-hourglass_bottom',
                cssClass: isFirstScanned ? 'is-success' : 'is-info',
                text: `${t('actions.scan_photos')}: ${isFirstScanned ? t('table.is_done') : t('table.is_waiting')}`,
                callback: () => showPhotosModal('isFirstScan')
              }} />
            </Div>
            <Div cssClass='column'>
              <h2>{t('titles.second_scan')}</h2>
              <Buttons
                buttons={[
                  {
                    icon: 'icon-access_time',
                    cssClass: treatmentJournals['secondScan'] && treatmentJournals['secondScan']['startedAt'] ? 'is-warning' : 'is-dark',
                    text: `${t('actions.started_at')}: ${treatmentJournals['secondScan'] ? treatmentJournals['secondScan']['startedAt'] : ''}`,
                    callback: () => updateJournal({ isStart: true })
                  }, {
                    icon: 'icon-access_time',
                    cssClass: 'undefined' === typeof treatmentJournals['secondScan'] ? 'is-hidden' : (treatmentJournals['secondScan'] && treatmentJournals['secondScan']['endedAt'] ? 'is-warning' : 'is-dark'),
                    text: `${t('actions.ended_at')}: ${treatmentJournals['secondScan'] ? treatmentJournals['secondScan']['endedAt'] : ''}`,
                    callback: () => updateJournal({ isEnd: true, id: treatmentJournals['secondScan']['id'] })
                  }
                ]} />
              <SwitcherType
                name='isSecondScanned'
                value={treatmentJournals['secondScan'] ? treatmentJournals['secondScan']['isDone'] : false}
                onClick={() => updateJournal({ isDone: true, id: treatmentJournals['secondScan'] ? treatmentJournals['secondScan']['id'] : null })}
                label={t('labels.is_second_scanned')}
                id={`is-${id}-second-scanned`}
              />
              <Buttons buttons={{
                icon: isSecondScanned ? 'icon-done_all' : 'icon-hourglass_bottom',
                cssClass: isSecondScanned ? 'is-success' : 'is-info',
                text: `${t('actions.scan_photos')}: ${isSecondScanned ? t('table.is_done') : t('table.is_waiting')}`,
                callback: () => showPhotosModal('isSecondScan')
              }} />
            </Div>
          </Div>
        </td>
      </tr>
    </>
  )
}

ActiveSterilizationCall.propTypes = {
  sterilizationCall: object,
  togglePhotosModal: func,
  setModalPhotoFilter: func
}

export default ActiveSterilizationCall